import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

export type FaVariant = "solid" | "regular" | "light";

// Cache SVG markup ที่ fetch มาแล้ว เพื่อไม่ให้โหลดซ้ำทุกครั้งที่ mount
const svgCache = new Map<string, string>();

interface IconProps {
  /** ชื่อไฟล์ icon เช่น "download", "arrow-right-from-bracket" */
  icon: string;
  /** โฟลเดอร์ของ icon ใน /public (fa-solid | fa-regular | fa-light) */
  variant?: FaVariant;
  className?: string;
  style?: CSSProperties;
  spin?: boolean;
}

/**
 * แทน <FontAwesomeIcon> โดยดึงไฟล์ SVG จาก /public/fa-{variant}/{icon}.svg
 * SVG ใช้ fill="currentColor" จึงกำหนดสีผ่าน CSS color และขนาดผ่าน font-size
 * ได้เหมือนเดิม
 */
const Icon = ({ icon, variant = "solid", className, style, spin }: IconProps) => {
  const cacheKey = `${variant}/${icon}`;
  const [svg, setSvg] = useState<string | null>(
    () => svgCache.get(cacheKey) ?? null
  );

  useEffect(() => {
    const cached = svgCache.get(cacheKey);
    if (cached) {
      setSvg(cached);
      return;
    }

    let cancelled = false;

    fetch(`${process.env.PUBLIC_URL}/fa-${cacheKey}.svg`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to load icon "${cacheKey}" (HTTP ${res.status})`
          );
        }
        return res.text();
      })
      .then((text) => {
        svgCache.set(cacheKey, text);
        if (!cancelled) setSvg(text);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setSvg("");
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  const classes = ["fa-icon", spin ? "fa-spin" : "", className]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={classes}
      style={style}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: svg ?? "" }}
    />
  );
};

export default Icon;
